// @ts-check
import { initSchema } from '@aws-amplify/datastore';
import { schema } from './schema';

const PartyRoleTypeEnum = {
  "PARENT_ENTITY": "ParentEntity",
  "SUBSIDIARY": "Subsidiary",
  "SUPPLIER": "Supplier",
  "TARGET": "Target",
  "MILESTONE": "Milestone",
  "INTERIM_TARGET": "InterimTarget"
};

const PartyRelationshipTypeEnum = {
  "ORGANISATION_ROLLUP": "OrganisationRollup",
  "TARGET_ROLLUP": "TargetRollup"
};

const RiskManagementProcessPolicySelectTypeEnum = {
  "PROCESSES": "Processes",
  "SUPPLEMENTARY": "Supplementary"
};

const ClimateRelatedTargetsSelectTypeEnum = {
  "CLIMATE_RELATED_TARGET": "ClimateRelatedTarget",
  "TARGET_SETTING_REVIEW_APPROACH": "TargetSettingReviewApproach",
  "PERFORMANCE_AGAINST_TARGET": "PerformanceAgainstTarget",
  "GREENHOUSE_GAS_EMISSION_TARGET_DATA": "GreenhouseGasEmissionTargetData"
};

const ClimateRelatedMetricsSelectTypeEnum = {
  "CROSS_INDUSTRY_METRICS": "CrossIndustryMetrics",
  "RISK_OPPORTUNITY_METRICS": "RiskOpportunityMetrics",
  "INDUSTRY_METRICS": "IndustryMetrics"
};

const FacilityOrSiteEnum = {
  "FACILITY": "Facility",
  "SITE": "Site"
};

const ClimateRelatedRiskOpportunitySelectTypeEnum = {
  "CLIMATE_RELATED_RISK_OPPORTUNITY": "ClimateRelatedRiskOpportunity",
  "BUSINESS_MODEL_VALUE_CHAIN": "BusinessModelValueChain",
  "FINANCIAL_EFFECT": "FinancialEffect"
};

const AppGroup = {
  "ADMINISTRATOR": "Administrator",
  "AUDITOR": "Auditor",
  "CONTRIBUTOR": "Contributor",
  "EDITOR": "Editor"
};

const TransitionPhysicalRiskEnum = {
  "PHYSICAL_RISK": "PhysicalRisk",
  "TRANSITION_RISK": "TransitionRisk"
};

const StatusEnum = {
  "DRAFT": "Draft",
  "PUBLISHED": "Published",
  "INACTIVE": "Inactive"
};

const EmissionCategoryEnum = {
  "CATEGORY1": "Category1",
  "CATEGORY2": "Category2",
  "CATEGORY3": "Category3",
  "CATEGORY4": "Category4",
  "CATEGORY5": "Category5",
  "CATEGORY6": "Category6",
  "CATEGORY7": "Category7",
  "CATEGORY8": "Category8",
  "CATEGORY9": "Category9",
  "CATEGORY10": "Category10",
  "CATEGORY11": "Category11",
  "CATEGORY12": "Category12",
  "CATEGORY13": "Category13",
  "CATEGORY14": "Category14",
  "CATEGORY15": "Category15"
};

const AssetActivityType = {
  "BUSINESS_ACTIVITY": "BusinessActivity",
  "ASSET": "Asset"
};

const CarbonAccountSelectTypeEnum = {
  "EMISSION_ACCOUNT": "EmissionAccount",
  "REDUCTION_ACCOUNT": "ReductionAccount",
  "OFFSET_ACCOUNT": "OffsetAccount",
  "BIOGENIC_ACCOUNT": "BiogenicAccount",
  "AVOIDED_EMISSION_ACCOUNT": "AvoidedEmissionAccount"
};

const ReductionInitiativeStatusEnum = {
  "INVESTIGATING": "Investigating",
  "PLANNING": "Planning",
  "IMPLEMENTING": "Implementing",
  "IMPLEMENTED": "Implemented"
};

const OffsetAchievementTypeEnum = {
  "CARBON_REDUCTION": "CarbonReduction",
  "CARBON_REMOVAL": "CarbonRemoval"
};

const CarbonCreditTypeEnum = {
  "NATURE_BASED": "NatureBased",
  "TECHNOLOGICAL_BASED": "TechnologicalBased"
};

const OffsetRelianceEnum = {
  "PERCENTAGE": "Percentage",
  "VALUE": "Value",
  "NONE": "None"
};

const SectoralDecarbonisationApproachEnum = {
  "USES_SECTORAL_DECARBONISATION_APPROACH": "UsesSectoralDecarbonisationApproach",
  "DOESNT_USE_SECTORAL_DECARBONISATION_APPROACH": "DoesntUseSectoralDecarbonisationApproach"
};

const TargetEmissionTypeEnum = {
  "GROSS_GHG_EMISSION_TARGET": "GrossGhgEmissionTarget",
  "NET_GHG_EMISSION_TARGET": "NetGhgEmissionTarget"
};

const EmissionScopeEnum = {
  "SCOPE_1": "Scope_1",
  "SCOPE_2": "Scope_2",
  "SCOPE_3": "Scope_3"
};

const GhgCoveredByTarget = {
  "CARBON_DIOXIDE": "CarbonDioxide",
  "METHANE": "Methane",
  "NITROUS_OXIDE": "NitrousOxide",
  "HYDROFLUOROCARBONS": "Hydrofluorocarbons",
  "PERFLUOROCARBONS": "Perfluorocarbons",
  "SULPHUR_HEXAFLUORIDE": "SulphurHexafluoride"
};

const TargetMethodologyValidationEnum = {
  "VALIDATED_BY_THIRD_PARTY": "ValidatedByThirdParty",
  "NOT_VALIDATED": "NotValidated"
};

const EmissionUnitEnum = {
  "EMPLOYEES": "Employees",
  "KG": "Kg",
  "KW": "Kw",
  "KWH": "Kwh",
  "LITRES": "Litres",
  "METRES_CUBED": "MetresCubed",
  "MILLION_DOLLARS_REVENUE": "MillionDollarsRevenue",
  "NIGHTS": "Nights",
  "PASSENGER_PER_KM": "PassengerPerKm",
  "PER_CAPITA": "PerCapita",
  "PERCENTAGE": "Percentage",
  "QUANTITY": "Quantity",
  "TONNES": "Tonnes",
  "TONNES_PER_KM": "TonnesPerKm"
};

const TargetTypeEnum = {
  "ABSOLUTE_TARGET": "AbsoluteTarget",
  "INTENSITY_TARGET": "IntensityTarget"
};

const TargetObjectiveEnum = {
  "MITIGATION": "Mitigation",
  "ADAPTATION": "Adaptation",
  "CONFORMANCE_WITH_SCIENCE_BASED_INITIATIVES": "ConformanceWithScienceBasedInitiatives"
};

const AmountPercentageEnum = {
  "AMOUNT": "Amount",
  "PERCENTAGE": "Percentage"
};

const ScopeOfOperationsEnum = {
  "COMPANY_WIDE": "CompanyWide",
  "VALUE_CHAIN_SEGMENT": "ValueChainSegment",
  "BUSINESS_DIVISION": "BusinessDivision",
  "FACILITY": "Facility",
  "COUNTRY_OR_AREA": "CountryOrArea",
  "PRODUCT_LEVEL": "ProductLevel",
  "PORTFOLIO_LEVEL": "PortfolioLevel",
  "OTHER": "Other"
};

const ScenarioTemperatureAlignmentEnum = {
  "ONE_POINT_FIVE_DEGREES_CELSIUS": "OnePointFiveDegreesCelsius",
  "ONE_POINT_SIX_TO_TWO_DEGREES_CELSIUS": "OnePointSixToTwoDegreesCelsius",
  "TWO_POINT_ONE_TO_THREE_DEGREES_CELSIUS": "TwoPointOneToThreeDegreesCelsius",
  "THREE_POINT_ONE_TO_FOUR_DEGREES_CELSIUS": "ThreePointOneToFourDegreesCelsius",
  "FOUR_POINT_ONE_DEGREES_AND_ABOVE": "FourPointOneDegreesAndAbove"
};

const ScenarioAnalysisMethodEnum = {
  "QUALITATIVE": "Qualitative",
  "QUANTITATIVE": "Quantitative",
  "BOTH": "Both"
};

const ClimateResilienceAssessmentTypeEnum = {
  "RESILIENCE_ASSESSMENT_OF_STRATEGY_AND_BUSINESS_MODEL": "ResilienceAssessmentOfStrategyAndBusinessModel",
  "IMPLICATIONS_OF_RESILIENCE_ASSESSMENT": "ImplicationsOfResilienceAssessment",
  "RESPONSE_TO_IDENTIFIED_EFFECTS_FROM_CLIMATE_RELATED_SCENARIO_ANALYSIS": "ResponseToIdentifiedEffectsFromClimateRelatedScenarioAnalysis",
  "AREA_OF_SIGNIFICANT_UNCERTAINTY": "AreaOfSignificantUncertainty",
  "AVAILABILITY_OF_FINANCIAL_RESOURCES": "AvailabilityOfFinancialResources",
  "FLEXIBILITY_OF_FINANCIAL_RESOURCES": "FlexibilityOfFinancialResources",
  "ABILITY_TO_MODIFY_EXISTING_ASSET_USAGE": "AbilityToModifyExistingAssetUsage",
  "EFFECT_OF_CURRENT_INVESTMENTS": "EffectOfCurrentInvestments",
  "EFFECT_OF_PLANNED_INVESTMENTS": "EffectOfPlannedInvestments"
};

const StrategyDecisionMakingDataSelectTypeEnum = {
  "TRANSITION_PLAN": "TransitionPlan",
  "TARGET_ACHIEVEMENT_PLAN": "TargetAchievementPlan",
  "STRATEGY_RESOURCING": "StrategyResourcing",
  "STRATEGY_PLAN_PROGRESS": "StrategyPlanProgress"
};

const FinancialPositionChangeTypeEnum = {
  "INVESTMENT_PLAN": "InvestmentPlan",
  "DISPOSAL_PLAN": "DisposalPlan",
  "FUNDING_SOURCE": "FundingSource",
  "FINANCIAL_PERFORMANCE_CHANGE": "FinancialPerformanceChange",
  "CASHFLOW_CHANGE": "CashflowChange"
};

const FinancialEffectTypeEnum = {
  "FINANCIAL_POSITION": "FinancialPosition",
  "FINANCIAL_PERFORMANCE": "FinancialPerformance",
  "CASHFLOWS": "Cashflows",
  "CARRYING_AMOUNT_OF_ASSETS": "CarryingAmountOfAssets",
  "CARRYING_AMOUNT_OF_LIABILITIES": "CarryingAmountOfLiabilities"
};

const PriorityEnum = {
  "LOWEST": "Lowest",
  "LOW": "Low",
  "MEDIUM": "Medium",
  "HIGH": "High",
  "HIGHEST": "Highest"
};

const LikelihoodOfOccurringEnum = {
  "NONE": "None",
  "LOW": "Low",
  "MEDIUM": "Medium",
  "HIGH": "High",
  "VERY_HIGH": "VeryHigh"
};

const RiskOpportunityGroupEnum = {
  "PHYSICAL_RISK": "PhysicalRisk",
  "ACUTE_PHYSICAL": "AcutePhysical",
  "CHRONIC_PHYSICAL": "ChronicPhysical",
  "TRANSITION_RISK": "TransitionRisk",
  "MARKET_RISK": "MarketRisk",
  "POLICY_AND_LEGAL_RISK": "PolicyAndLegalRisk",
  "REPUTATION_RISK": "ReputationRisk",
  "TECHNOLOGY_RISK": "TechnologyRisk",
  "RESOURCE_EFFICIENCY_OPPORTUNITY": "ResourceEfficiencyOpportunity",
  "ENERGY_SOURCE_OPPORTUNITY": "EnergySourceOpportunity",
  "PRODUCTS_SERVICES_OPPORTUNITY": "ProductsServicesOpportunity",
  "MARKETS_OPPORTUNITY": "MarketsOpportunity",
  "RESILIENCE_OPPORTUNITY": "ResilienceOpportunity"
};

const RiskOpportunityTypeEnum = {
  "RISK": "Risk",
  "OPPORTUNITY": "Opportunity"
};

const ClimateOversightProcessTypeEnum = {
  "STRATEGY_OVERSIGHT": "StrategyOversight",
  "MAJOR_TRANSACTION_DECISION_MAKING": "MajorTransactionDecisionMaking",
  "RISK_MANAGEMENT_PROCESSES": "RiskManagementProcesses",
  "POLICIES": "Policies"
};

const ProcessTypeEnum = {
  "ASSESS": "Assess",
  "DECIDE": "Decide",
  "IDENTIFY": "Identify",
  "MANAGE": "Manage",
  "MONITOR": "Monitor",
  "OVERSEE": "Oversee",
  "PRIORITISE": "Prioritise",
  "REPORT": "Report",
  "INTEGRATED": "Integrated"
};

const ProcessFrequencyEnum = {
  "DAILY": "Daily",
  "WEEKLY": "Weekly",
  "FORTNIGHTLY": "Fortnightly",
  "MONTHLY": "Monthly",
  "QUARTERLY": "Quarterly",
  "HALF_YEARLY": "HalfYearly",
  "YEARLY": "Yearly",
  "EVERY_TWO_YEARS": "EveryTwoYears",
  "EVERY_THREE_YEARS": "EveryThreeYears",
  "ADHOC": "Adhoc",
  "NEVER": "Never"
};

const GovernanceProcessSelectTypeEnum = {
  "INFORM_GOVERNANCE": "InformGovernance",
  "CLIMATE_STRATEGY_OVERSIGHT": "ClimateStrategyOversight",
  "GOVERNANCE_METRICS_TARGETS": "GovernanceMetricsTargets",
  "MANAGEMENT_OVERSIGHT": "ManagementOversight",
  "GOVERNANCE_ARTIFACT": "GovernanceArtifact"
};

const GovernanceDataSelectTypeEnum = {
  "GOVERNANCE_BODY": "GovernanceBody",
  "MANAGEMENT_GOVERNANCE_ROLE": "ManagementGovernanceRole"
};

const ClimatePlanningHorizonsEnum = {
  "CURRENT_REPORTING_PERIOD": "CurrentReportingPeriod",
  "NEXT_REPORTING_PERIOD": "NextReportingPeriod",
  "SHORT_TERM": "ShortTerm",
  "MEDIUM_TERM": "MediumTerm",
  "LONG_TERM": "LongTerm"
};

const OrgIdTypeEnum = {
  "ABN": "ABN",
  "BUSINESS_NUMBER": "BusinessNumber",
  "COMPANY_NUMBER": "CompanyNumber",
  "DUNS_NUMBER": "DunsNumber",
  "IATI": "IATI",
  "NZBN": "NZBN",
  "REGISTRATION_NUMBER": "RegistrationNumber",
  "STOCK_SYMBOL": "StockSymbol"
};

const CurrencyEnum = {
  "AUD": "AUD",
  "CAD": "CAD",
  "CNY": "CNY",
  "HKD": "HKD",
  "NZD": "NZD",
  "SGD": "SGD",
  "GBP": "GBP",
  "USD": "USD"
};

const CountryEnum = {
  "AUSTRALIA": "Australia",
  "CANADA": "Canada",
  "CHINA": "China",
  "HONG_KONG": "HongKong",
  "NEW_ZEALAND": "NewZealand",
  "SINGAPORE": "Singapore",
  "UNITED_KINGDOM": "UnitedKingdom",
  "UNITED_STATES": "UnitedStates"
};

const PublishedToEnum = {
  "PUBLIC": "Public",
  "STAKEHOLDER": "Stakeholder",
  "ASSURANCE": "Assurance",
  "REGULATOR": "Regulator"
};

const { FinancialEffect, PartyRelationship, AuditLog, Evidence, Comment, AvoidedEmissionAccountEntry, BiogenicAccountEntry, Location, Asset, AssetRegister, AppUsers, OffsetAccountEntry, EmissionAccountEntry, CarbonAccount, CarbonAccountingLedger, ReductionAccountEntry, ClimateRelatedTargets, ClimateRelatedMetrics, RiskManagementProcessPolicy, ScenarioAnalysisAssumptions, ScenarioAnalysis, ClimateResilienceAssessment, ClimateResilience, StrategyDecisionMakingData, MitigationAdaptationChange, BusinessModelChange, StrategyDecisionMaking, AggregatedFinancialEffect, RiskOpportunity, ClimateRelatedRiskOpportunity, ClimateGovernanceProcess, GovernanceData, BusinessPlanningHorizon, ClimatePlanningHorizon, PlanningHorizon, MetricAndTargetDataCollection, RiskManagementDataCollection, StrategyDataCollection, GovernanceDataCollection, ReportingEntity, ReportingPeriod, Disclosure, CarbonAccountAvoidedEmissionAccountEntry, CarbonAccountBiogenicAccountEntry, AssetLocation, ReportingEntityLocation, AssetAssetRegister, ClimateRelatedMetricsAsset, OffsetAccountEntryCarbonAccount, OffsetAccountEntryClimateRelatedTargets, EmissionAccountEntryCarbonAccount, ReductionAccountEntryCarbonAccount, CarbonAccountCarbonAccountingLedger, ClimateRelatedTargetsReductionAccountEntry, ClimateRelatedTargetsMetricAndTargetDataCollection, ClimateRelatedMetricsClimateRelatedTargets, ClimateRelatedMetricsMetricAndTargetDataCollection, RiskManagementProcessPolicyRiskManagementDataCollection, RiskOpportunityRiskManagementProcessPolicy, ScenarioAnalysisAssumptionsClimateResilience, ScenarioAnalysisClimateResilience, ReportingPeriodScenarioAnalysis, ClimateResilienceClimateResilienceAssessment, ClimateResilienceStrategyDataCollection, StrategyDecisionMakingDataStrategyDecisionMaking, MitigationAdaptationChangeStrategyDecisionMaking, BusinessModelChangeStrategyDecisionMaking, StrategyDecisionMakingStrategyDataCollection, AggregatedFinancialEffectClimateRelatedRiskOpportunity, RiskOpportunityClimateRelatedRiskOpportunity, ClimateRelatedRiskOpportunityStrategyDataCollection, PlanningHorizonClimateRelatedRiskOpportunity, ClimateGovernanceProcessGovernanceData, GovernanceDataGovernanceDataCollection, BusinessPlanningHorizonClimatePlanningHorizon, ClimatePlanningHorizonPlanningHorizon } = initSchema(schema);

export {
  FinancialEffect,
  PartyRelationship,
  AuditLog,
  Evidence,
  Comment,
  AvoidedEmissionAccountEntry,
  BiogenicAccountEntry,
  Location,
  Asset,
  AssetRegister,
  AppUsers,
  OffsetAccountEntry,
  EmissionAccountEntry,
  CarbonAccount,
  CarbonAccountingLedger,
  ReductionAccountEntry,
  ClimateRelatedTargets,
  ClimateRelatedMetrics,
  RiskManagementProcessPolicy,
  ScenarioAnalysisAssumptions,
  ScenarioAnalysis,
  ClimateResilienceAssessment,
  ClimateResilience,
  StrategyDecisionMakingData,
  MitigationAdaptationChange,
  BusinessModelChange,
  StrategyDecisionMaking,
  AggregatedFinancialEffect,
  RiskOpportunity,
  ClimateRelatedRiskOpportunity,
  ClimateGovernanceProcess,
  GovernanceData,
  BusinessPlanningHorizon,
  ClimatePlanningHorizon,
  PlanningHorizon,
  MetricAndTargetDataCollection,
  RiskManagementDataCollection,
  StrategyDataCollection,
  GovernanceDataCollection,
  ReportingEntity,
  ReportingPeriod,
  Disclosure,
  CarbonAccountAvoidedEmissionAccountEntry,
  CarbonAccountBiogenicAccountEntry,
  AssetLocation,
  ReportingEntityLocation,
  AssetAssetRegister,
  ClimateRelatedMetricsAsset,
  OffsetAccountEntryCarbonAccount,
  OffsetAccountEntryClimateRelatedTargets,
  EmissionAccountEntryCarbonAccount,
  ReductionAccountEntryCarbonAccount,
  CarbonAccountCarbonAccountingLedger,
  ClimateRelatedTargetsReductionAccountEntry,
  ClimateRelatedTargetsMetricAndTargetDataCollection,
  ClimateRelatedMetricsClimateRelatedTargets,
  ClimateRelatedMetricsMetricAndTargetDataCollection,
  RiskManagementProcessPolicyRiskManagementDataCollection,
  RiskOpportunityRiskManagementProcessPolicy,
  ScenarioAnalysisAssumptionsClimateResilience,
  ScenarioAnalysisClimateResilience,
  ReportingPeriodScenarioAnalysis,
  ClimateResilienceClimateResilienceAssessment,
  ClimateResilienceStrategyDataCollection,
  StrategyDecisionMakingDataStrategyDecisionMaking,
  MitigationAdaptationChangeStrategyDecisionMaking,
  BusinessModelChangeStrategyDecisionMaking,
  StrategyDecisionMakingStrategyDataCollection,
  AggregatedFinancialEffectClimateRelatedRiskOpportunity,
  RiskOpportunityClimateRelatedRiskOpportunity,
  ClimateRelatedRiskOpportunityStrategyDataCollection,
  PlanningHorizonClimateRelatedRiskOpportunity,
  ClimateGovernanceProcessGovernanceData,
  GovernanceDataGovernanceDataCollection,
  BusinessPlanningHorizonClimatePlanningHorizon,
  ClimatePlanningHorizonPlanningHorizon,
  PartyRoleTypeEnum,
  PartyRelationshipTypeEnum,
  RiskManagementProcessPolicySelectTypeEnum,
  ClimateRelatedTargetsSelectTypeEnum,
  ClimateRelatedMetricsSelectTypeEnum,
  FacilityOrSiteEnum,
  ClimateRelatedRiskOpportunitySelectTypeEnum,
  AppGroup,
  TransitionPhysicalRiskEnum,
  StatusEnum,
  EmissionCategoryEnum,
  AssetActivityType,
  CarbonAccountSelectTypeEnum,
  ReductionInitiativeStatusEnum,
  OffsetAchievementTypeEnum,
  CarbonCreditTypeEnum,
  OffsetRelianceEnum,
  SectoralDecarbonisationApproachEnum,
  TargetEmissionTypeEnum,
  EmissionScopeEnum,
  GhgCoveredByTarget,
  TargetMethodologyValidationEnum,
  EmissionUnitEnum,
  TargetTypeEnum,
  TargetObjectiveEnum,
  AmountPercentageEnum,
  ScopeOfOperationsEnum,
  ScenarioTemperatureAlignmentEnum,
  ScenarioAnalysisMethodEnum,
  ClimateResilienceAssessmentTypeEnum,
  StrategyDecisionMakingDataSelectTypeEnum,
  FinancialPositionChangeTypeEnum,
  FinancialEffectTypeEnum,
  PriorityEnum,
  LikelihoodOfOccurringEnum,
  RiskOpportunityGroupEnum,
  RiskOpportunityTypeEnum,
  ClimateOversightProcessTypeEnum,
  ProcessTypeEnum,
  ProcessFrequencyEnum,
  GovernanceProcessSelectTypeEnum,
  GovernanceDataSelectTypeEnum,
  ClimatePlanningHorizonsEnum,
  OrgIdTypeEnum,
  CurrencyEnum,
  CountryEnum,
  PublishedToEnum
};